import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface ComptableResult {
  proposed_entry: {
    rows: Array<{
      sequence_number: number;
      entry_date: string;
      fiscal_year: number;
      journal_code: string;
      debit_account: string;
      credit_account: string;
      amount_millimes: number;
      label: string;
      piece_reference?: string;
    }>;
    total_debit_millimes: number;
    total_credit_millimes: number;
    is_balanced: boolean;
    findings: Array<{ severity: 'error' | 'warning' | 'info'; code: string; description: string }>;
  };
  vat_breakdown: { ht_millimes: number; vat_millimes: number; ttc_millimes: number; vat_rate: number };
  classification: { sce_code: string; confidence: number; method: string; reasoning: string };
}

export function ComptablePage() {
  const [opType, setOpType] = useState<'purchase' | 'sale'>('purchase');
  const [vendor, setVendor] = useState('STEG');
  const [label, setLabel] = useState('Facture STEG mars 2026');
  const [amountTnd, setAmountTnd] = useState('1000.000');
  const [vatRate, setVatRate] = useState<0 | 7 | 13 | 19>(19);
  const [entryDate, setEntryDate] = useState('2026-04-15');
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [seqNum, setSeqNum] = useState(100);
  const [journalCode, setJournalCode] = useState('AC');
  const [pieceRef, setPieceRef] = useState('F-2026-0042');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        operation_type: opType,
        vendor_name: vendor,
        invoice_label: label,
        amount_ht_millimes: tndStringToMillimes(amountTnd),
        vat_rate: vatRate,
        entry_date: entryDate,
        fiscal_year: fiscalYear,
        next_sequence_number: seqNum,
        journal_code: journalCode,
      };
      if (pieceRef.trim()) payload.piece_reference = pieceRef.trim();

      const res = await api.invokeAgent('comptable', {
        payload,
        fiscal_reference_date: entryDate,
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Comptable"
      module="Comptabilité · Phase 1"
      model="Sonnet 4.6"
      description={
        <>
          Génère une écriture comptable conforme au plan SCE NC 01 — classification automatique du
          compte (cache cabinet → règles déterministes → escalade Sonnet), calcul TVA via engine-tva,
          validation d'équilibre + numérotation séquentielle via engine-journal-sce.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Opération">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Type d'opération">
              <select className={inputCls} value={opType} onChange={(e) => setOpType(e.target.value as 'purchase' | 'sale')}>
                <option value="purchase">Achat (facture fournisseur)</option>
                <option value="sale">Vente (facture client)</option>
              </select>
            </Field>
            <Field label="Code journal" hint="AC = achats · VE = ventes · BQ = banque · OD = OD diverses">
              <input className={inputCls + ' font-mono'} value={journalCode} onChange={(e) => setJournalCode(e.target.value.toUpperCase())} maxLength={8} required />
            </Field>
            <Field label={opType === 'purchase' ? 'Nom du fournisseur' : 'Nom du client'}>
              <input className={inputCls} value={vendor} onChange={(e) => setVendor(e.target.value)} required />
            </Field>
            <Field label="Libellé pièce">
              <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} required />
            </Field>
            <Field label="Montant HT (TND)">
              <input
                className={inputCls + ' font-mono'}
                value={amountTnd}
                onChange={(e) => setAmountTnd(e.target.value)}
                placeholder="1000.000"
                required
              />
            </Field>
            <Field label="Taux TVA">
              <select className={inputCls} value={vatRate} onChange={(e) => setVatRate(parseInt(e.target.value, 10) as 0 | 7 | 13 | 19)}>
                <option value={19}>19% (standard)</option>
                <option value={13}>13% (réduit)</option>
                <option value={7}>7% (très réduit)</option>
                <option value={0}>0% (exonéré / export)</option>
              </select>
            </Field>
            <Field label="Date pièce">
              <input type="date" className={inputCls} value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
            </Field>
            <Field label="Année fiscale">
              <input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} />
            </Field>
            <Field label="N° séquence" hint="Pré-rempli par l'API en prod (MAX(seq)+1 sur D1)">
              <input type="number" className={inputCls + ' font-mono'} value={seqNum} onChange={(e) => setSeqNum(parseInt(e.target.value || '1', 10))} min={1} />
            </Field>
            <Field label="Référence pièce" hint="N° de la facture / pièce justificative (optionnel)">
              <input className={inputCls + ' font-mono'} value={pieceRef} onChange={(e) => setPieceRef(e.target.value)} />
            </Field>
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Génération…' : 'Générer l\'écriture'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <ComptableResultView result={result as ComptableResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function ComptableResultView({ result }: { result: ComptableResult }) {
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Décomposition TVA</div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Stat label="HT" value={formatMillimes(result.vat_breakdown.ht_millimes)} />
          <Stat label={`TVA ${result.vat_breakdown.vat_rate}%`} value={formatMillimes(result.vat_breakdown.vat_millimes)} />
          <Stat label="TTC" value={formatMillimes(result.vat_breakdown.ttc_millimes)} highlight />
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Classification SCE NC 01</div>
        <div className="p-3 rounded bg-cream/50 border border-border">
          <div className="flex items-center gap-3">
            <span className="font-mono text-2xl font-bold">{result.classification.sce_code}</span>
            <span className="text-xs px-2 py-0.5 rounded bg-zinc-100 border border-zinc-300 uppercase tracking-wider text-taupe">
              {result.classification.method}
            </span>
            <span className="text-xs text-taupe">
              {(result.classification.confidence * 100).toFixed(0)}% confiance
            </span>
          </div>
          <div className="text-xs text-taupe mt-1">{result.classification.reasoning}</div>
        </div>
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-taupe">Écriture proposée</div>
          <div className="text-xs">
            <span className={result.proposed_entry.is_balanced ? 'text-emerald-700' : 'text-red-700'}>
              {result.proposed_entry.is_balanced ? '✓ Équilibrée' : '✗ Non équilibrée'}
            </span>
          </div>
        </div>
        {result.proposed_entry.rows.length === 0 ? (
          <div className="p-4 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900">
            Aucune ligne d'écriture émise — voir les hypothèses ci-dessus.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                  <th className="px-2 py-2 text-left">Seq.</th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Journal</th>
                  <th className="px-2 py-2 text-left">Compte D</th>
                  <th className="px-2 py-2 text-left">Compte C</th>
                  <th className="px-2 py-2 text-right">Montant</th>
                  <th className="px-2 py-2 text-left">Libellé</th>
                </tr>
              </thead>
              <tbody>
                {result.proposed_entry.rows.map((r) => (
                  <tr key={r.sequence_number} className="border-b border-border/50">
                    <td className="px-2 py-1.5 font-mono">{r.sequence_number}</td>
                    <td className="px-2 py-1.5 font-mono">{r.entry_date}</td>
                    <td className="px-2 py-1.5 font-mono">{r.journal_code}</td>
                    <td className="px-2 py-1.5 font-mono font-bold text-emerald-800">{r.debit_account}</td>
                    <td className="px-2 py-1.5 font-mono font-bold text-rose-800">{r.credit_account}</td>
                    <td className="px-2 py-1.5 font-mono text-right">{formatMillimes(r.amount_millimes)}</td>
                    <td className="px-2 py-1.5 text-xs text-taupe">{r.label}</td>
                  </tr>
                ))}
                <tr className="bg-cream/40 font-medium">
                  <td className="px-2 py-1.5" colSpan={5}>Totaux</td>
                  <td className="px-2 py-1.5 font-mono text-right">
                    {formatMillimes(result.proposed_entry.total_debit_millimes)} / {formatMillimes(result.proposed_entry.total_credit_millimes)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {result.proposed_entry.findings.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Findings moteur</div>
          <ul className="text-sm space-y-1">
            {result.proposed_entry.findings.map((f, i) => (
              <li key={i} className={`p-2 rounded text-xs border ${
                f.severity === 'error'
                  ? 'border-red-300 bg-red-50 text-red-900'
                  : f.severity === 'warning'
                  ? 'border-amber-300 bg-amber-50 text-amber-900'
                  : 'border-zinc-300 bg-zinc-50 text-zinc-700'
              }`}>
                <span className="font-mono uppercase tracking-wider">{f.code}</span> · {f.description}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-lg ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
